import { forwardRef, type ReactNode } from 'react';
import { clx } from '@medusajs/ui';

import { variantStyles, type TextVariant } from './variant-styles';
import type { ClassValue } from '@modules/common/types';

type TextElement =
  | 'span'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'div'
  | 'label';

type TextProps = {
  as?: TextElement;
  variant: TextVariant;
  children?: ReactNode;
  className?: ClassValue;
} & Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'className'>;

function defaultElementForVariant(variant: TextVariant): TextElement {
  if (variant.startsWith('h1')) return 'h1';
  if (variant.startsWith('h2')) return 'h2';
  if (variant.startsWith('h3')) return 'h3';
  if (variant.startsWith('title')) return 'h4';
  return 'span';
}

const Text = forwardRef<HTMLElement, TextProps>(
  ({ as, variant, className, children, ...props }, ref) => {
    const Tag = as ?? defaultElementForVariant(variant);

    return (
      <Tag ref={ref as React.Ref<never>} className={clx(variantStyles[variant], className)} {...props}>
        {children}
      </Tag>
    );
  }
);

Text.displayName = 'Text';

export default Text;
export type { TextVariant, TextElement, TextProps };
